import { httpsCallable } from 'firebase/functions';
import {
  collection, doc, getDoc, query, where, orderBy, onSnapshot, getDocs,
} from 'firebase/firestore';
import { db, functions } from '../firebase.js';

// ─────────────────────────────────────────────────────────────────────────
// Marketplace — client library
//
// Reading listings/purchases is a plain Firestore read (see firestore.rules:
// marketplace/ is readable by any signed-in user, purchases/{uid} readable
// only by that buyer). Every WRITE that matters — publishing a listing,
// approving a version for distribution, taking payment, handing over
// purchased content — goes through Cloud Functions (functions/marketplace.js)
// so ownership/entitlement checks happen server-side. See that file for the
// DUMMY PAYMENT GATEWAY notice — checkout here always "succeeds"; there is
// no real charge yet.
// ─────────────────────────────────────────────────────────────────────────

const listForSaleFn = httpsCallable(functions, 'listForSale');
const quotePurchaseFn = httpsCallable(functions, 'quotePurchase');
const delistListingFn = httpsCallable(functions, 'delistListing');
const relistListingFn = httpsCallable(functions, 'relistListing');
const deleteListingForeverFn = httpsCallable(functions, 'deleteListingForever');
const approveVersionFn = httpsCallable(functions, 'approveVersion');
const purchaseListingFn = httpsCallable(functions, 'purchaseListing');
const reportPurchaseFn = httpsCallable(functions, 'reportPurchase');
const getPurchaseContentFn = httpsCallable(functions, 'getPurchaseContent');
const getListingPreviewFn = httpsCallable(functions, 'getListingPreview');
const voteListingFn = httpsCallable(functions, 'voteListing');
const filePurchaseFn = httpsCallable(functions, 'filePurchase');
const deleteCourseForeverFn = httpsCallable(functions, 'deleteCourseForever');
const deleteTopicForeverFn = httpsCallable(functions, 'deleteTopicForever');
const searchMarketplaceListingsSemanticFn = httpsCallable(functions, 'searchMarketplaceListingsSemantic');

export async function listForSale(payload) {
  const { data } = await listForSaleFn({ ...payload, idempotencyKey: crypto.randomUUID() });
  return data; // { listingId }
}

export async function delistListing(listingId) {
  await delistListingFn({ listingId, idempotencyKey: crypto.randomUUID() });
}

export async function relistListing(listingId) {
  await relistListingFn({ listingId, idempotencyKey: crypto.randomUUID() });
}

// Actually deletes the listing doc — unlike delistListing (pause) above,
// this can't be undone with relistListing. Throws failed-precondition if any
// buyer still has active, unexpired access to THIS listing (delist it and
// wait for access to lapse instead — see functions/marketplace.js
// deleteListingForever for why that has to be enforced server-side).
export async function deleteListingForever(listingId) {
  await deleteListingForeverFn({ listingId, idempotencyKey: crypto.randomUUID() });
}

export async function approveVersion(listingId, topicId = null) {
  const { data } = await approveVersionFn({ listingId, topicId, idempotencyKey: crypto.randomUUID() });
  return data;
}

// Everything the checkout dialog shows before the buyer commits: list
// price, their school discount (if a code is linked), final payable amount,
// and current wallet balance — all computed server-side so the dialog can
// never disagree with what purchaseListing will actually charge.
export async function quotePurchase(listingId) {
  const { data } = await quotePurchaseFn({ listingId });
  return data; // { currency, listPaise, payablePaise, walletBalancePaise, accessDuration }
}

// Grants rendered-page access only — Learning Lab does not sell source code,
// so there is no tier/download option to pass here (see functions/marketplace.js).
// WALLET-ONLY: debits the buyer's wallet; throws failed-precondition
// ("Insufficient wallet balance…") when the balance is short.
export async function purchaseListing(listingId) {
  const { data } = await purchaseListingFn({ listingId, idempotencyKey: crypto.randomUUID() });
  return data; // { purchaseId, pricePaidPaise, expiresAt }
}

export async function reportPurchase(purchaseId, complaint) {
  const { data } = await reportPurchaseFn({ purchaseId, complaint, idempotencyKey: crypto.randomUUID() });
  return data; // { ok, complaintId }
}

export async function getPurchaseContent(purchaseId) {
  const { data } = await getPurchaseContentFn({ purchaseId });
  return data; // { type, title, courseId, sellerUid, items: [{ topicId, title, html }] }
}

// Plain one-time read of a listing doc's own fields (price, approvedVersion(s),
// etc.) — NOT its content. marketplace/{listingId} is readable by any
// signed-in user (firestore.rules), so this is a direct Firestore read,
// no Cloud Function involved.
export async function getListing(listingId) {
  const snap = await getDoc(doc(db, 'marketplace', listingId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

// Free teaser preview for the Browse tab — same content a buyer would get,
// but the client wraps it with an interaction-blocking + fade-at-halfway
// overlay before rendering (see views/marketplace.js wrapHtmlForPreview).
export async function getListingPreview(listingId) {
  const { data } = await getListingPreviewFn({ listingId });
  return data; // { type, title, items: [{ topicId, title, html }] }
}

// Casts (or changes, or retracts with value=null) the caller's vote on a
// listing they purchased — enforced server-side (see functions/marketplace.js
// voteListing) since a security rule can't check "did this uid ever buy
// this listing" against a private purchases subcollection. Returns the
// updated aggregate counts plus the caller's resulting vote so the UI can
// update immediately without waiting on a snapshot listener.
export async function voteListing(listingId, value) {
  const { data } = await voteListingFn({ listingId, value, idempotencyKey: crypto.randomUUID() });
  return data; // { upvotes, downvotes, yourVote }
}

// One-time read of the caller's own vote on a listing (null if they haven't
// voted). Used to highlight the active up/down button when reopening a
// purchase — this is a plain owner-scoped read (marketplace/{listingId}/votes/{uid}
// is only readable by that uid, see firestore.rules), not a Cloud Function call.
export async function getMyVote(uid, listingId) {
  const snap = await getDoc(doc(db, 'marketplace', listingId, 'votes', uid));
  return snap.exists() ? snap.data().value : null;
}

// Files (or, passing courseId: null, unfiles) a purchase under one of the
// buyer's own courses/topics — purely organizational. This never copies or
// moves the seller's actual content; it just tags the buyer's own purchase
// record with where they want it to show up in their tree (see
// functions/marketplace.js filePurchase for why this has to go through a
// Cloud Function rather than a direct client write).
export async function filePurchase(purchaseId, courseId, topicId = null) {
  await filePurchaseFn({ purchaseId, courseId, topicId, idempotencyKey: crypto.randomUUID() });
}

// Guarded PERMANENT delete — replaces the client-only deleteCourse/deleteTopic
// (src/lib/courses.js, src/lib/topics.js) for "Delete forever" and account
// deletion. Throws (failed-precondition) if any buyer still has active,
// unexpired access to this content — see functions/contentLifecycle.js for
// why that check has to happen server-side. Purchased content can never be
// resold, and the original author can't delete it out from under someone
// who currently has access; it can only be deleted once everyone's access
// has expired.
export async function deleteCourseForever(courseId) {
  await deleteCourseForeverFn({ courseId, idempotencyKey: crypto.randomUUID() });
}

export async function deleteTopicForever(courseId, topicId) {
  await deleteTopicForeverFn({ courseId, topicId, idempotencyKey: crypto.randomUUID() });
}

// All of the caller's purchases filed at the top level of a given course
// (filedTopicId is unset) plus, separately, ones filed under a specific
// topic — course.js merges these into its tree view. Sorted client-side
// (same pattern as watchMyPurchases) rather than via orderBy, so no
// composite index is needed for what's otherwise a single-field filter.
export function watchFiledPurchases(uid, courseId, callback) {
  const q = query(collection(db, 'users', uid, 'purchases'), where('filedCourseId', '==', courseId));
  return onSnapshot(q, (snap) => {
    const purchases = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    purchases.sort((a, b) => (b.purchasedAt?.toMillis?.() || 0) - (a.purchasedAt?.toMillis?.() || 0));
    callback(purchases);
  });
}

// One-time fetch of every purchase the caller has ever made — used by the
// "File under a course/topic" picker (both in course.js and the Marketplace
// "My purchases" tab) rather than a live subscription, since it's opened
// inside a modal that closes right after use.
export async function getMyPurchasesOnce(uid) {
  const snap = await getDocs(collection(db, 'users', uid, 'purchases'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ── Browse: active listings, newest first ──────────────────────────────
// sellerIsAdmin == true restricts Browse to admin-curated content — see
// functions/marketplace.js listForSaleImpl, which stamps that field on
// every listing it writes (and only lets an admin uid create a NEW one in
// the first place). A listing from before that policy simply has no such
// field, so this filter excludes it automatically with no backfill needed.
export function watchActiveListings(callback) {
  const q = query(collection(db, 'marketplace'), where('status', '==', 'active'), where('sellerIsAdmin', '==', true), orderBy('createdAt', 'desc'));
  return onSnapshot(q, (snap) => callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
}

// Admin-curated labels ("Preferred list" etc. — see
// functions/marketplaceLabels.js). Readable directly by any signed-in user
// (firestore.rules) since it's just a name + which listing IDs belong to
// it; writes only ever happen through the admin panel's callables.
export function watchMarketplaceLabels(callback) {
  const q = query(collection(db, 'marketplaceLabels'), orderBy('createdAt', 'asc'));
  return onSnapshot(q, (snap) => callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
}

// One-time keyword search over active, already-approved listings — used by
// the "New topic" dialog (see openTopicModal in views/course.js) to warn a
// creator BEFORE they spend AI generation on something that already exists
// in the marketplace. Same plain substring match as the Browse tab's own
// search box (title + courseTitle + description) — instant (no network
// round trip), so both call sites keep this as their default/first layer
// and additionally call searchMarketplaceListingsSemantic (below) for
// paraphrase/synonym recall a plain substring match misses. excludeUid
// omits the searching creator's own listings (no point warning someone
// about their own content).
export async function searchMarketplaceListings(keywords, { excludeUid = null, limit = 3 } = {}) {
  const term = String(keywords || '').trim().toLowerCase();
  if (term.length < 3) return [];
  const q = query(collection(db, 'marketplace'), where('status', '==', 'active'), where('sellerIsAdmin', '==', true));
  const snap = await getDocs(q);
  const words = term.split(/\s+/).filter(Boolean);
  const matches = snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((l) => excludeUid ? l.sellerUid !== excludeUid : true)
    .filter((l) => {
      const hasApproved = l.type === 'topic' ? !!l.approvedVersion : !!(l.approvedVersions && Object.keys(l.approvedVersions).length);
      if (!hasApproved) return false;
      const haystack = `${l.title} ${l.courseTitle || ''} ${l.description || ''}`.toLowerCase();
      // Match if the whole typed phrase appears, or at least half the
      // individual words do — catches close-but-not-identical titles
      // ("Circle Theorems" vs "Circle Theorem Basics") without being so
      // loose that a single common word triggers a false "already exists".
      if (haystack.includes(term)) return true;
      const hits = words.filter((w) => w.length > 2 && haystack.includes(w)).length;
      return words.length > 1 && hits >= Math.ceil(words.length / 2);
    });
  matches.sort((a, b) => (b.subscriberCount || 0) - (a.subscriberCount || 0) || (b.salesCount || 0) - (a.salesCount || 0));
  return matches.slice(0, limit);
}

// Semantic (embedding) companion to searchMarketplaceListings above — see
// functions/search.js. Slower (a real Cloud Function round trip, not an
// in-memory filter), so callers keep the plain substring search as their
// instant default and layer this in once it resolves, not as a replacement.
export async function searchMarketplaceListingsSemantic(query, { excludeUid = null, limit = 3 } = {}) {
  const res = await searchMarketplaceListingsSemanticFn({ query, excludeUid, limit });
  return res.data.results;
}

// ── A seller's own listings (active + delisted) ────────────────────────
export function watchMyListings(uid, callback) {
  const q = query(collection(db, 'marketplace'), where('sellerUid', '==', uid));
  return onSnapshot(q, (snap) => {
    const listings = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    listings.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
    callback(listings);
  });
}

// ── A buyer's own purchases ─────────────────────────────────────────────
export function watchMyPurchases(uid, callback) {
  const q = query(collection(db, 'users', uid, 'purchases'));
  return onSnapshot(q, (snap) => {
    const purchases = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    purchases.sort((a, b) => (b.purchasedAt?.toMillis?.() || 0) - (a.purchasedAt?.toMillis?.() || 0));
    callback(purchases);
  });
}

export function isExpired(purchase) {
  // Every purchase now expires. Marketplace subscriptions are fixed at one
  // year. A missing expiresAt can only be legacy pre-launch test data;
  // treat it as still-valid rather than breaking those records.
  if (!purchase.expiresAt) return false;
  const ms = purchase.expiresAt?.toMillis?.() ?? purchase.expiresAt;
  return ms < Date.now();
}

// ── A seller's own sale records — earning + "Processing fee" only, written
// by purchaseListing (see functions/marketplace.js for what deliberately
// ISN'T in these docs). ───────────────────────────────────────────────────
export function watchMySales(uid, callback) {
  const q = query(collection(db, 'users', uid, 'sales'));
  return onSnapshot(q, (snap) => {
    const sales = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    sales.sort((a, b) => (b.soldAt?.toMillis?.() || 0) - (a.soldAt?.toMillis?.() || 0));
    callback(sales);
  });
}

// Fetch the listing for a single course/topic if one already exists, so
// the "List for sale" UI can default to editing rather than always
// creating a duplicate listing.
export async function findExistingListing(uid, courseId, topicId = null) {
  const q = query(collection(db, 'marketplace'), where('sellerUid', '==', uid), where('courseId', '==', courseId));
  const snap = await getDocs(q);
  const match = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
    .find((l) => (topicId ? l.topicId === topicId : l.type === 'course'));
  return match || null;
}

// After a creator finalizes a new visual layout, refresh every already-
// published listing that distributes this topic. Course listings are included
// because their approvedVersions map also contains individual topic versions.
// Listings that have never been approved stay drafts and are not published by
// this convenience sync.
export async function syncPublishedListingsForTopic(uid, courseId, topicId) {
  const q = query(collection(db, 'marketplace'), where('sellerUid', '==', uid), where('courseId', '==', courseId));
  const snap = await getDocs(q);
  const listings = snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((listing) => {
    if (!listing.approvedAt) return false;
    return listing.type === 'course'
      ? Object.prototype.hasOwnProperty.call(listing.approvedVersions || {}, topicId)
      : listing.topicId === topicId;
  });
  await Promise.all(listings.map((listing) => approveVersion(listing.id, topicId)));
  return listings.length;
}

// Marketplace subscriptions are fixed at one year.
export const ACCESS_DURATION_LABELS = {
  '1y': '1 year',
};
